import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as TextIcon,
  TableChart as SpreadsheetIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  CloudUpload as UploadIcon,
  Visibility as PreviewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { attachmentService } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AttachmentList = ({ relatedType, relatedId, canUpload = true }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState({ open: false, file: null, url: null, text: null });

  useEffect(() => {
    fetchAttachments();
  }, [relatedType, relatedId]);

  // Revoke object URL on close to avoid memory leaks
  useEffect(() => {
    if (!preview.open && preview.url) {
      URL.revokeObjectURL(preview.url);
    }
  }, [preview.open]);

  const fetchAttachments = async () => {
    try {
      setLoading(true);
      const response = await attachmentService.getByRelated(relatedType, relatedId);
      setAttachments(response.data.data);
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('related_type', relatedType);
      formData.append('related_id', relatedId);

      await attachmentService.upload(formData);
      fetchAttachments();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Erreur lors du téléchargement du fichier');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce fichier ?')) return;
    try {
      await attachmentService.delete(id);
      setAttachments(attachments.filter(a => a.id !== id));
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const fetchBlob = useCallback(async (id) => {
    const token = localStorage.getItem('era_token');
    const response = await fetch(`${API_URL}/attachments/download/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Fetch failed');
    return response.blob();
  }, []);

  const downloadFile = async (id, filename) => {
    try {
      const blob = await fetchBlob(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Download failed', e);
      alert('Erreur lors du téléchargement');
    }
  };

  const openPreview = async (file) => {
    try {
      const blob = await fetchBlob(file.id);
      const mime = file.mime_type || '';

      if (mime.startsWith('text/') || mime === 'application/json') {
        const text = await blob.text();
        setPreview({ open: true, file, url: null, text });
      } else {
        const url = URL.createObjectURL(blob);
        setPreview({ open: true, file, url, text: null });
      }
    } catch (e) {
      console.error('Preview failed', e);
      alert('Impossible de prévisualiser ce fichier');
    }
  };

  const closePreview = () => {
    setPreview(prev => ({ ...prev, open: false }));
  };

  const isPreviewable = (mimeType) => {
    if (!mimeType) return false;
    return (
      mimeType.startsWith('image/') ||
      mimeType === 'application/pdf' ||
      mimeType.startsWith('text/') ||
      mimeType === 'application/json'
    );
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return <FileIcon color="action" />;
    if (mimeType.startsWith('image/')) return <ImageIcon color="primary" />;
    if (mimeType === 'application/pdf') return <PdfIcon color="error" />;
    if (mimeType.startsWith('text/') || mimeType === 'application/json') return <TextIcon color="info" />;
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <SpreadsheetIcon color="success" />;
    return <FileIcon color="action" />;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderPreviewContent = () => {
    const { file, url, text } = preview;
    if (!file) return null;
    const mime = file.mime_type || '';

    if (mime.startsWith('image/')) {
      return (
        <Box sx={{ textAlign: 'center', p: 1 }}>
          <img
            src={url}
            alt={file.original_name}
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 4 }}
          />
        </Box>
      );
    }

    if (mime === 'application/pdf') {
      return (
        <Box sx={{ width: '100%', height: '75vh' }}>
          <object
            data={url}
            type="application/pdf"
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          >
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>
                Votre navigateur ne peut pas afficher ce PDF.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadFile(file.id, file.original_name)}
              >
                Télécharger le PDF
              </Button>
            </Box>
          </object>
        </Box>
      );
    }

    if (mime.startsWith('text/') || mime === 'application/json') {
      return (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            overflow: 'auto',
            maxHeight: '70vh',
            fontSize: '0.8rem',
            fontFamily: 'monospace',
            bgcolor: 'grey.100',
            borderRadius: 1,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all'
          }}
        >
          {text}
        </Box>
      );
    }

    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <FileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography color="text.secondary">
          Aperçu non disponible pour ce type de fichier.
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Pièces jointes</Typography>
        {canUpload && (
          <Button
            component="label"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
            disabled={uploading}
          >
            Ajouter un fichier
            <input type="file" hidden onChange={handleFileUpload} />
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={30} />
        </Box>
      ) : attachments.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
          <Typography color="text.secondary">Aucune pièce jointe</Typography>
        </Paper>
      ) : (
        <List dense disablePadding>
          {attachments.map((file) => (
            <Paper key={file.id} variant="outlined" sx={{ mb: 1 }}>
              <ListItem
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {isPreviewable(file.mime_type) && (
                      <Tooltip title="Visualiser">
                        <IconButton size="small" onClick={() => openPreview(file)}>
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Télécharger">
                      <IconButton size="small" onClick={() => downloadFile(file.id, file.original_name)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canUpload && (
                      <Tooltip title="Supprimer">
                        <IconButton size="small" onClick={() => handleDelete(file.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                }
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {getFileIcon(file.mime_type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {file.original_name}
                      </Typography>
                      {isPreviewable(file.mime_type) && (
                        <Chip label="Aperçu" size="small" variant="outlined" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  }
                  secondary={`${formatSize(file.size)} • ${file.uploader_name} • ${new Date(file.created_at).toLocaleDateString('fr-FR')}`}
                />
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={preview.open}
        onClose={closePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '95vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
            {preview.file && getFileIcon(preview.file.mime_type)}
            <Typography variant="subtitle1" noWrap>
              {preview.file?.original_name}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closePreview}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: preview.file?.mime_type === 'application/pdf' ? 0 : 2, overflow: 'hidden' }}>
          {renderPreviewContent()}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => downloadFile(preview.file?.id, preview.file?.original_name)}
            size="small"
          >
            Télécharger
          </Button>
          <Button onClick={closePreview} size="small">Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttachmentList;
